import { useState } from "react";
import { AlertTriangle, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { feedbackApi } from "@/api/services";
import { useProjectStore } from "@/stores/project";
import { useAppErrorNoticeStore } from "@/stores/appErrorNotice";

/**
 * Felix's own word when a request fails (row f14fbbc8; the store's header says
 * why and how the connection is ruled out).
 *
 *   * app failure: the app has the problem, not the user; Felix proposes a ticket
 *     and files it on one click. The ticket goes through POST /feedback/, so a
 *     person's ticket also becomes the agents' backlog row (row f186d562).
 *   * user mistake: guidance only, no ticket.
 *
 * No AI call: the wording and the ticket are built from the failure itself.
 */
export function FelixAppErrorNotice() {
  const notice = useAppErrorNoticeStore((s) => s.notice);
  const dismiss = useAppErrorNoticeStore((s) => s.dismiss);
  const projectId = useProjectStore((s) => s.selectedProjectId);
  const [filing, setFiling] = useState(false);
  const [filed, setFiled] = useState<{ at: number; code: string } | null>(null);
  const [failedAt, setFailedAt] = useState<number | null>(null);

  if (!notice) return null;
  const filedCode = filed && filed.at === notice.at ? filed.code : null;
  const fileFailed = failedAt === notice.at;

  const fileTicket = async () => {
    if (!notice.ticket) return;
    setFiling(true);
    try {
      const t = await feedbackApi.create({
        ...notice.ticket,
        project_id: projectId ?? undefined,
        category: "GENERAL",
        severity: "HIGH",
        error_messages: [notice.ticket.title],
      });
      setFiled({ at: notice.at, code: t.code });
    } catch {
      setFailedAt(notice.at);
    } finally {
      setFiling(false);
    }
  };

  if (notice.kind === "user") {
    return (
      <div
        data-testid="felix-app-error-guidance"
        className="shrink-0 border-t border-blue-300/50 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-xs"
      >
        <div className="flex items-start gap-2">
          <Info className="h-3.5 w-3.5 mt-0.5 text-blue-600 dark:text-blue-300 shrink-0" />
          <div className="flex-1 space-y-1">
            <p className="text-text-primary">That didn't go through: {notice.message}</p>
            <p className="text-muted-foreground">Nothing is wrong with the app. Fix that and try again.</p>
          </div>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={dismiss}>
            Got it
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="felix-app-error-notice"
      className="shrink-0 border-t border-amber-300/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-600 dark:text-amber-300 shrink-0" />
        <div className="flex-1 space-y-1.5">
          <p className="text-text-primary">{notice.message}</p>
          {filedCode ? (
            <p className="text-muted-foreground">
              I filed ticket {filedCode}. The team has it, and you'll hear back here when it's fixed.
            </p>
          ) : fileFailed ? (
            <p className="text-muted-foreground">
              I couldn't file the ticket just now. You can report it from the Feedback page.
            </p>
          ) : (
            <p className="text-muted-foreground">
              Shall I file a support ticket so the team fixes it? I'll include what failed.
            </p>
          )}
          <div className="flex gap-1.5">
            {!filedCode && !fileFailed && (
              <Button size="sm" className="h-6 px-2 text-xs" onClick={fileTicket} disabled={filing}>
                {filing && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                File a ticket
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={dismiss}>
              {filedCode || fileFailed ? "Close" : "Not now"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
